<template>
  <!-- pt from the safe-area inset: the header is sticky, so the top inset has to live here
       rather than on the shell, or it would scroll away from the notch. -->
  <header class="public-nav sticky top-0 z-50 border-b border-base-300/80 bg-base-100/95 pt-[env(safe-area-inset-top,0px)] backdrop-blur-md dark:bg-base-200/95">
    <div class="mx-auto flex h-16 max-w-7xl items-center gap-2 px-3 sm:gap-4 sm:px-6 lg:px-8">
      <router-link :to="routeManager.homePath()" class="tmi-logo min-w-0" aria-label="True Market Insiders home"></router-link>

      <nav class="hidden min-w-0 flex-1 items-center justify-center md:flex" aria-label="Main">
        <ul class="flex items-center gap-1">
          <li v-if="showProductsMenuItem">
            <details
              id="products-menu"
              class="dropdown dropdown-hover"
              @mouseenter="showMenuOnFocus"
              @mouseleave="hideMenuOnBlur"
            >
              <summary class="public-nav-link">{{ labelService.label( 'lblProducts' ) }}</summary>
              <ul class="menu dropdown-content w-72" @click="hideMenuOnBlur">
                <template v-if="showProductBrochures">
                  <li class="menu-title text-jade pl-2 pb-0 uppercase">{{ labelService.label( 'lblAvailableServices' ) }}</li>
                  <li v-for="product in productBrochures" :key="product.slug">
                    <router-link :to="routeManager.productRouteBySlug( product.slug )">
                      <img loading="lazy" v-if="product.favicon" :src="product.favicon" class="h-6 w-6 rounded-lg" alt="">
                      <span>{{ product.name }}</span>
                    </router-link>
                  </li>
                </template>
              </ul>
            </details>
          </li>
          <li>
            <router-link :to="routeManager.articleSearchPath()" class="public-nav-link">
              {{ labelService.label( 'lblArticles' ) }}
            </router-link>
          </li>
          <li>
            <router-link :to="routeManager.sectorEdgePath()" class="public-nav-link">
              {{ labelService.label( 'lblSectorEdge' ) }}
            </router-link>
          </li>
          <li>
            <router-link :to="contactUsPath" class="public-nav-link">
              {{ labelService.label( 'lblContact' ) }}
            </router-link>
          </li>
        </ul>
      </nav>

      <!-- shrink-0 on the cluster, not just its children: as a nested flex container its automatic
           minimum size was not holding, so flexbox squeezed the cluster's BOX below its content
           (141px around 157px of buttons) and pushed the menu button off-screen instead of
           shrinking the logo, which still had room to give. -->
      <div class="ml-auto flex shrink-0 items-center gap-1 sm:gap-3">
        <router-link
          :to="routeManager.signInPath()"
          class="btn btn-primary btn-sm inline-flex min-h-11 shrink-0 px-3 text-sm font-semibold no-underline sm:min-h-10 sm:px-5"
        >
          {{ labelService.label( 'lblLogIn' ) }}
        </router-link>
        <DarkModeToggle />
        <button
          type="button"
          class="btn btn-ghost btn-square min-h-11 min-w-11 md:hidden"
          :aria-expanded="showMobileMenu"
          aria-controls="public-mobile-menu"
          @click="showMobileMenu = !showMobileMenu"
        >
          <span class="sr-only">Menu</span>
          <i class="pi text-lg" :class="showMobileMenu ? 'pi-times' : 'pi-bars'"></i>
        </button>
      </div>
    </div>

    <!-- max-h + scroll: with Products expanded this list used to run past the bottom of a
         375x667 phone with no way to reach the last items, because the sticky header clips it. -->
    <div
      v-if="showMobileMenu"
      id="public-mobile-menu"
      class="max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain border-t border-base-300 bg-base-100 pb-[env(safe-area-inset-bottom,0px)] md:hidden dark:bg-base-200"
    >
      <nav class="mx-auto max-w-7xl px-4 py-4 sm:px-6" aria-label="Mobile">
        <ul class="menu menu-lg gap-1 p-0">
          <li v-if="showProductsMenuItem">
            <details>
              <summary class="font-semibold">{{ labelService.label( 'lblProducts' ) }}</summary>
              <ul>
                <template v-if="showProductBrochures">
                  <li v-for="product in productBrochures" :key="product.slug">
                    <router-link :to="routeManager.productRouteBySlug( product.slug )">
                      <img loading="lazy" v-if="product.favicon" :src="product.favicon" class="h-6 w-6 rounded-lg" alt="">
                      <span>{{ product.name }}</span>
                    </router-link>
                  </li>
                </template>
              </ul>
            </details>
          </li>
          <li>
            <router-link :to="routeManager.articleSearchPath()" class="font-semibold">
              {{ labelService.label( 'lblArticles' ) }}
            </router-link>
          </li>
          <li>
            <router-link :to="routeManager.sectorEdgePath()" class="font-semibold">
              {{ labelService.label( 'lblSectorEdge' ) }}
            </router-link>
          </li>
          <li>
            <router-link :to="contactUsPath" class="font-semibold">
              {{ labelService.label( 'lblContact' ) }}
            </router-link>
          </li>
        </ul>
        <router-link
          :to="routeManager.signInPath()"
          class="btn btn-primary btn-block mt-4 font-semibold no-underline"
        >
          {{ labelService.label( 'lblLogIn' ) }}
        </router-link>
      </nav>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import { storeToRefs } from "pinia";
import { Router, useRouter } from "vue-router";
import routeManager from "@/router/route-manager";

import DarkModeToggle from '@/components/layout/DarkModeToggle.vue';

import type { Product } from "@tmi/types/entities";
import { useProductsStore } from "@/stores/Products";
const productsStore = useProductsStore();
const { availableProducts } = storeToRefs( productsStore );

import { useMenuPermissions } from "@/composables/menu-permissions";
const { showProductsMenuItem } = useMenuPermissions();

import type { LabelService } from "@/composables/label-service";
import { labelServiceSymbol } from "@/plugins/LabelServicePlugin";

const labelService = inject< LabelService >( labelServiceSymbol ) as LabelService;

const router: Router = useRouter();

const contactUsPath = ref< string >( routeManager.contactUsPath() );

const hasAvailableProducts = computed< boolean >( () => availableProducts.value?.length > 0 );

const showProductBrochures = computed< boolean >( () => hasAvailableProducts.value );
const productBrochures = computed< Product[] >( () => {
  let products: Product[] = [];
  availableProducts.value.forEach( ( product: Product ) => products.push( product ) );
  return products;
} );

const showMobileMenu = ref< boolean >( false );
router.beforeEach( () => {
  showMobileMenu.value = false;
} );

// Close the <details> itself. On @mouseleave the target IS the <details>, but on the dropdown's
// @click the target is whichever link was clicked, so removing `open` from it did nothing and the
// menu stayed open after navigating — most visible on touch, where there is no mouseleave at all.
function hideMenuOnBlur ( event: Event ) {
  const details = ( event.target as Element | null )?.closest( "details" );
  details?.removeAttribute( "open" );
}

function showMenuOnFocus ( event: Event ) {
  if ( event.target ) {
    ( event.target as Element ).setAttribute( "open", "" );
  }
}
</script>

<style scoped>
/*
  The bar used to need a 397px viewport: an 11rem shrink-0 logo plus a shrink-0 login button,
  theme toggle and menu button. Below that the menu button ran off the right edge and
  `body { overflow-x: hidden }` clipped it (61px off-screen at 320px — unreachable). The logo now
  shrinks down to a 7rem floor; background-size:contain scales it proportionally inside the box.
*/
a.tmi-logo {
  display: block;
  width: 11rem;
  min-width: 7rem;
  flex-shrink: 1;
  height: 2.25rem;
  background-image: url("/img/logos/logo-green.svg");
  background-repeat: no-repeat;
  background-size: contain;
  background-position: left center;
}

[data-theme="dark"] a.tmi-logo {
  background-image: url("/img/logos/logo-white.svg");
}

.public-nav-link {
  display: inline-flex;
  align-items: center;
  min-height: 2.5rem;
  padding: 0 1rem;
  border-radius: 0.375rem;
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1.25;
  color: var(--color-base-content);
  text-decoration: none;
  white-space: nowrap;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.public-nav-link:hover,
.public-nav :deep(summary.public-nav-link:hover) {
  background-color: color-mix(in oklch, var(--color-base-content) 8%, transparent);
}

.public-nav-link.router-link-exact-active,
.public-nav :deep(.dropdown:has(ul a.router-link-active) summary.public-nav-link) {
  background-color: var(--color-base-200);
  font-weight: 600;
}

.public-nav :deep(summary.public-nav-link) {
  list-style: none;
  cursor: pointer;
}

.public-nav :deep(summary.public-nav-link::-webkit-details-marker) {
  display: none;
}

/*
  Touch targets in the header control cluster. The theme toggle is a daisyUI btn-sm (32px) and the
  menu button was 40px — both under the 44px minimum, in the only row an anonymous visitor has to
  touch. Matches what PrivateHeader does for its own controls, and relaxes at md where the desktop
  nav takes over and a mouse is driving.
*/
@media (max-width: 767px) {
  .public-nav :deep(.btn) {
    min-height: 2.75rem;
    min-width: 2.75rem;
  }
}

/*
  Touch targets in the mobile menu. daisyUI's menu-lg rows measured 39px tall and only as wide as
  their text, so the tap target for "Articles" was 133x39 rather than the full row. 44px is the
  platform minimum on both iOS and Android.
*/
#public-mobile-menu :deep(a),
#public-mobile-menu :deep(summary) {
  min-height: 2.75rem;
  display: flex;
  align-items: center;
  width: 100%;
}
</style>
